<template>
    <div
        class="grid gap-y-0 grid-cols-12 text-sm"
        style="grid-template-columns: 120px repeat(11, minmax(0, 1fr))"
        rowBreakpoint="sm"
    >
        <div class="mb-4 col-span-12 flex justify-between items-center">
            <div class="font-extrabold text-base">
                {{ props.estimatedCost ? 'Timeline of Estimated Cost' : 'Timeline of Messages' }}
            </div>
            <div>
                <vx-button
                    @click="exportData"
                    :loading="exporting"
                    color="muted-dark"
                    size="sm"
                    outline
                    data-test="export-btn"
                >
                    <span class="inline sm:hidden md:inline">Export</span>
                    <font-awesome-icon :icon="faFileArrowDown" />
                </vx-button>
            </div>
        </div>

        <div
            v-if="data.length"
            isHeader
            class="px-2 -ml-2 items-center col-span-12 hidden sm:grid grid-cols-subgrid pb-4"
        >
            <div class="col-span-1 font-semibold">{{ props.estimatedCost ? 'Cost' : 'Messages' }}</div>
            <div :class="[FEATURES.contact_groups ? 'col-span-2' : 'col-span-4', 'font-semibold']">Date</div>
            <div v-if="FEATURES.contact_groups" class="col-span-4 font-semibold">Senders</div>
            <div class="col-span-3 font-semibold">Blasts</div>

            <div class="col-span-2 flex justify-end">
                <vx-button @click="toggleAll" color="muted-dark" class="-mr-2" size="sm" data-test="toggle-all-btn">
                    Toggle All
                    <font-awesome-icon :icon="showingAll ? faSquareMinus : faSquarePlus" />
                </vx-button>
            </div>
        </div>

        <div class="overflow-hidden border border-slate-100 grid col-span-12 grid-cols-subgrid rounded shadow">
            <blast-forecast-table-row
                v-for="(item, index) in data"
                ref="items"
                :key="item.date.toISOString()"
                :item="item"
                :fetchParams="fetchParams"
                :timezone="timezone"
                :nextItem="data[index + 1]"
                :orgUnitId="props.orgUnitId"
                :showEstimatedCost="props.showEstimatedCost"
                :estimatedCost="props.estimatedCost"
            />
        </div>
    </div>
</template>

<script setup lang="ts">
import BlastForecastTableRow from './BlastForecastTableRow.vue';
import { inject, nextTick, ref, useTemplateRef } from 'vue';
import type {
    GetBlastForecastRequest,
    BlastForecastCampaignsResponse,
} from '~/services/blastForecast/blastForecastClient.types';
import { useBlastForecastSummary, type BlastForecastItemAggregated } from '../composables/useBlastForecastSummary';
import dayjs, { toUTCDateTimeFormat } from '~/utils/dayjs';
import { download, prepareCsv } from '~/utils/files';
import blastForecastClient from '~/services/blastForecast/blastForecastClient';
import { catchRequestError } from '~/utils/request';
import { useToasted } from '~/composables/useToasted';
import type { Features } from '~/provides/features.types';
import { faFileArrowDown, faSquareMinus, faSquarePlus } from '@fortawesome/pro-solid-svg-icons';
import { VxButton } from '@voxie/frontend-components';
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';

const FEATURES = inject('FEATURES') as Features;

const teamId = Spark.state.currentTeam.id;

const props = defineProps<{
    fetchParams: GetBlastForecastRequest;
    timezone: string;
    orgUnitId?: number;
    showEstimatedCost: boolean;
    estimatedCost: boolean;
}>();

const { data } = useBlastForecastSummary();

const rowsRef = useTemplateRef<InstanceType<typeof BlastForecastTableRow>[]>('items');

const showingAll = ref(false);
const exporting = ref(false);

const toasted = useToasted();

const splitRangeIntoMonths = (range: [string, string]): [string, string][] => {
    const start = dayjs.utc(range[0]).tz(props.timezone);
    const end = dayjs.utc(range[1]).tz(props.timezone);

    if (end.diff(start, 'day') <= 32) {
        return [range];
    }

    const chunks: [string, string][] = [];
    let current = start;

    while (current.isBefore(end)) {
        const nextMonth = current.startOf('month').add(1, 'month');
        const chunkEnd = nextMonth.isBefore(end) ? nextMonth : end;
        chunks.push([toUTCDateTimeFormat(current.startOf('hour')), toUTCDateTimeFormat(chunkEnd.startOf('hour'))]);
        current = chunkEnd;
    }

    return chunks;
};

const exportData = async () => {
    if (exporting.value) {
        return;
    }

    exporting.value = true;

    const headers = [
        'id',
        'name',
        'status',
        'live_at',
        'audience_size',
        'sms_segments',
        'main_message_body',
        'main_message_media_url',
        'main_message_media_content_type',
    ];

    if (FEATURES.contact_groups) {
        headers.push(...['sender_type', 'sender_id', 'sender_name']);
    }

    if (props.showEstimatedCost) {
        headers.push('estimated_cost');
    }

    const context = { teamId, orgUnitId: props.orgUnitId };
    const chunks = splitRangeIntoMonths(props.fetchParams.range);

    const requests = chunks.map((chunk) =>
        blastForecastClient.getBlastForecastCampaigns(context, {
            ...props.fetchParams,
            range: chunk,
        })
    );

    Promise.all(requests)
        .then((responses) => {
            const items = responses.flatMap((response) => response.data.items);
            const rows = items.map((campaign) => {
                const row = {
                    id: campaign.id,
                    name: campaign.name,
                    status: campaign.status,
                    live_at: campaign.live_at,
                    audience_size: campaign.audience_size,
                    sms_segments: campaign.sms_segments,
                    main_message_body: campaign.main_message?.body,
                    main_message_media_url: campaign.main_message?.media_url,
                    main_message_media_content_type: campaign.main_message?.media_content_type,
                    sender_type: FEATURES.contact_groups ? campaign.sender?.type : undefined,
                    sender_id: FEATURES.contact_groups ? campaign.sender?.id : undefined,
                    sender_name: FEATURES.contact_groups ? campaign.sender?.name : undefined,
                    estimated_cost: props.showEstimatedCost ? campaign.estimated_cost : undefined,
                };

                return row;
            });

            const csv = prepareCsv(headers, rows);
            download(csv, 'voxie_campaigns.csv', { type: 'text/csv;charset=utf-8;' });
        })
        .catch((e) => {
            toasted.error(catchRequestError(e));
        })
        .finally(() => (exporting.value = false));
};

const toggleAll = async () => {
    showingAll.value = !showingAll.value;

    if (!showingAll.value) {
        rowsRef.value?.forEach((row) => row.closeCampaigns());
        return;
    }

    rowsRef.value?.forEach((row) => row.openCampaignsDeferred());

    const context = { teamId, orgUnitId: props.orgUnitId };
    const chunks = splitRangeIntoMonths(props.fetchParams.range);

    const requests = chunks.map((chunk) =>
        blastForecastClient.getBlastForecastCampaigns(context, {
            ...props.fetchParams,
            range: chunk,
        })
    );

    try {
        const responses = await Promise.all(requests);
        const allItems = responses.flatMap((response) => response.data.items);

        const grouped = new Map<string, BlastForecastCampaignsResponse['items']>();
        for (const item of allItems) {
            const dateKey = dayjs.utc(item.live_at).tz(props.timezone).format('YYYY-MM-DD');
            const existing = grouped.get(dateKey);
            if (existing) {
                existing.push(item);
            } else {
                grouped.set(dateKey, [item]);
            }
        }

        rowsRef.value?.forEach((row, index) => {
            const item = data.value[index];
            if (!item) {
                return;
            }
            const dateKey = dayjs.utc(item.date).tz(props.timezone).format('YYYY-MM-DD');
            row.setCampaigns(grouped.get(dateKey) ?? []);
        });
    } catch (e) {
        toasted.error(catchRequestError(e));
        rowsRef.value?.forEach((row) => row.setCampaignsError());
    }
};

const openItem = async (toOpen: BlastForecastItemAggregated) => {
    showingAll.value = true;
    toggleAll();
    const index = data.value.findIndex((item) => dayjs.utc(item.date).tz(props.timezone).isSame(toOpen.date, 'day'));

    if (index === -1) {
        console.error('Day not found in summary data');
        return;
    }

    const row = rowsRef.value?.[index];

    if (!row) {
        console.error('Row not found in rowsRef');
        return;
    }

    const element: HTMLDivElement | undefined = row?.$el?.nextElementSibling;

    if (!element) {
        console.error('nextElementSibling not found in row');
        return;
    }

    row.openCampaigns();
    await nextTick();

    // timeout is necessary because the data might not be there,
    // causing the scrollTo ending up at the wrong position
    setTimeout(() => {
        window.scrollTo({
            top: window.scrollY + element.getBoundingClientRect().top - 80,
            behavior: 'smooth',
        });
    }, 500);
};

defineExpose({
    openItem,
});
</script>
